import { inject } from '@angular/core';
import { ConfigurableGridColumn } from '@common/components/column-configurator/column-configurator.component';
import { GridColumnSettingsService } from '@common/services/grid-column-settings.service';
import { GridComponent } from '@progress/kendo-angular-grid';

/**
 * Owns the per-grid column-visibility state (isVisible/toggle/reset/persist) behind
 * `<app-column-configurator>`, shared by every host regardless of which base class it extends -
 * see .claude/notes/column-configurator.md. Instantiate as a component field (not inside
 * ngOnInit/constructor body) so `inject()` runs within the host's own injection context, then
 * call `refresh()` from `ngOnInit`.
 */
export class GridColumnConfigController {
    visibility: Record<string, boolean> = {};
    /** Column keys in the user's preferred order; empty = template-declared order. */
    order: string[] = [];

    private readonly gridColumnSettingsService = inject(GridColumnSettingsService);

    constructor(
        private readonly gridKey: string,
        private readonly getColumns: () => ConfigurableGridColumn[],
        private readonly getGrid?: () => GridComponent | undefined,
        /**
         * Re-lays the grid out after a column was shown, hidden or moved. Hosts built on `BaseKendoList` /
         * `ExtendedBaseTab` pass `() => this.refreshColumnLayout()`, so the stretch of the last column follows
         * (and the hosts that opt out of stretching by overriding `expandLastColumnToFit` stay as they are).
         * Without it the grid is only re-fitted with a plain `autoFitColumns()`.
         */
        private readonly onLayoutChange?: () => void
    ) {}

    refresh(): void {
        const keys = this.getColumns().map((column) => column.key);
        this.order = this.gridColumnSettingsService.getOrder(this.gridKey);
        this.visibility = this.normalize(
            this.gridColumnSettingsService.getVisibility(this.gridKey, keys, this.defaults)
        );
    }

    isVisible(columnKey: string): boolean {
        return this.isLocked(columnKey) || this.visibility[columnKey] !== false;
    }

    isHidden(columnKey: string): boolean {
        return !this.isVisible(columnKey);
    }

    /** The columns the grid shows right now, in the order it shows them (locked ones lead). */
    visibleColumnsInOrder(): ConfigurableGridColumn[] {
        const locked = this.getColumns().filter((column) => column.locked);
        return [...locked, ...this.movableColumnsInOrder()].filter((column) => this.isVisible(column.key));
    }

    onVisibilityChange(columnKey: string, event: Event | boolean): void {
        if (event instanceof Event) {
            event.stopPropagation();
        }
        if (this.isLocked(columnKey)) {
            return;
        }
        const visible = typeof event === 'boolean' ? event : (event.target as HTMLInputElement)?.checked ?? true;
        const next = this.normalize({ ...this.visibility, [columnKey]: visible });
        if (next[columnKey] !== visible) {
            // Hiding the last non-locked column next to a locked one - see normalize().
            return;
        }
        this.gridColumnSettingsService.setColumnVisibility(
            this.gridKey,
            columnKey,
            visible,
            this.visibility,
            this.defaults
        );
        this.visibility = next;
        this.autoFit();
    }

    setAllVisibility(visible: boolean, event?: Event): void {
        event?.stopPropagation();
        const keys = this.getColumns()
            .filter((column) => !column.locked)
            .map((column) => column.key);
        const requested = keys.reduce((result, key) => ({ ...result, [key]: visible }), {} as Record<string, boolean>);
        const next = this.normalize(requested);
        this.gridColumnSettingsService.setAllColumnsVisibility(this.gridKey, keys, visible, this.defaults);
        const kept = keys.find((key) => next[key] !== visible);
        if (kept) {
            this.gridColumnSettingsService.setColumnVisibility(
                this.gridKey,
                kept,
                next[kept],
                requested,
                this.defaults
            );
        }
        this.visibility = next;
        this.autoFit();
    }

    /** The grid itself is reordered by `<app-column-configurator>`, which receives `order` as an input. */
    onOrderChange(newOrder: string[]): void {
        // Locked columns always lead, so that is what "the declared order" means.
        const columns = this.getColumns();
        const declaredOrder = [...columns.filter((c) => c.locked), ...columns.filter((c) => !c.locked)].map(
            (column) => column.key
        );
        const isDeclaredOrder =
            newOrder.length === declaredOrder.length && newOrder.every((key, index) => key === declaredOrder[index]);
        this.order = isDeclaredOrder ? [] : newOrder;
        this.gridColumnSettingsService.setOrder(this.gridKey, this.order);
        // the configurator moves the grid columns a microtask after this, the layout needs to follow
        if (this.onLayoutChange) {
            this.autoFit();
        }
    }

    reset(event?: Event): void {
        event?.stopPropagation();
        const keys = this.getColumns().map((column) => column.key);
        this.gridColumnSettingsService.resetToDefault(this.gridKey);
        this.order = [];
        this.visibility = this.normalize(
            this.gridColumnSettingsService.getVisibility(this.gridKey, keys, this.defaults)
        );
        this.autoFit();
    }

    private isLocked(columnKey: string): boolean {
        return this.getColumns().some((column) => column.key === columnKey && column.locked);
    }

    /**
     * Two rules the grid itself needs: a locked column can't be hidden (whatever an older saved layout
     * says), and with a locked column present at least one non-locked column must stay visible -
     * Kendo throws "There should be at least one non-locked column" otherwise. When nothing is left,
     * the first column in the user's order is kept.
     */
    private normalize(visibility: Record<string, boolean>): Record<string, boolean> {
        const result = { ...visibility };
        const locked = this.getColumns().filter((column) => column.locked);
        locked.forEach((column) => (result[column.key] = true));

        if (locked.length > 0) {
            const movable = this.movableColumnsInOrder();
            if (movable.length > 0 && movable.every((column) => result[column.key] === false)) {
                result[movable[0].key] = true;
            }
        }
        return result;
    }

    /** Non-locked columns that are offered right now, in the user's order. */
    private movableColumnsInOrder(): ConfigurableGridColumn[] {
        const rank = (column: ConfigurableGridColumn) => {
            const position = this.order.indexOf(column.key);
            return position === -1 ? Number.MAX_SAFE_INTEGER : position;
        };
        return this.getColumns()
            .filter((column) => !column.locked && (column.isAvailable?.() ?? true))
            .sort((a, b) => rank(a) - rank(b));
    }

    private get defaults(): Record<string, boolean> {
        return this.getColumns().reduce(
            (defaults, column) => {
                defaults[column.key] = column.defaultVisible ?? true;
                return defaults;
            },
            {} as Record<string, boolean>
        );
    }

    private autoFit(): void {
        setTimeout(() => (this.onLayoutChange ? this.onLayoutChange() : this.getGrid?.()?.autoFitColumns()));
    }
}
